// Accessible collapsible sidebar navigation for the handheld/mobile layout.
//
// On small screens the sidebar link list is presented as a dropdown: a toggle
// button reveals/hides the list of links. On larger screens the toggle button
// is hidden via CSS and the list is always visible, so this behaviour is a
// progressive enhancement that does nothing on desktop.

let sidebarLinkListController

const closeMenu = (toggle) => {
  toggle.setAttribute("aria-expanded", "false")
}

const openMenu = (toggle) => {
  toggle.setAttribute("aria-expanded", "true")
}

// The dropdown behaviour only exists on small screens, where the toggle button
// is visible. On larger screens the toggle is hidden via CSS and the list is
// always shown, so we must not hijack keys like Arrow/Home/End there (otherwise
// Home/End would jump between sidebar links instead of scrolling the page).
const isDropdownActive = (toggle) => window.getComputedStyle(toggle).display !== "none"

// Convenience keyboard navigation. Because the collapsed list looks like a
// native dropdown, sighted keyboard users may instinctively press the arrow
// keys, so we support Up/Down and Home/End to move focus between the links.
// This is a progressive enhancement layered on top of the disclosure pattern:
// Tab/Shift+Tab still work normally and we deliberately do not add role="menu".
const focusLinkByArrow = (event, toggle, list) => {
  const { key } = event
  if (key !== "ArrowDown" && key !== "ArrowUp" && key !== "Home" && key !== "End") {
    return
  }

  const links = Array.from(list.querySelectorAll("a"))
  if (!links.length) {
    return
  }

  event.preventDefault()

  // Open the list first if the user starts arrowing while it is collapsed.
  if (toggle.getAttribute("aria-expanded") !== "true") {
    openMenu(toggle)
  }

  const currentIndex = links.indexOf(document.activeElement)

  let nextLink
  if (key === "Home") {
    nextLink = links[0]
  } else if (key === "End") {
    nextLink = links[links.length - 1]
  } else if (key === "ArrowDown") {
    nextLink = links[currentIndex + 1] || links[0]
  } else {
    nextLink = links[currentIndex - 1] || links[links.length - 1]
  }

  nextLink.focus()
}

// Reflect the page the user is currently on in the toggle label, so that when
// the menu is collapsed, it reads like a native dropdown showing the selected
// option. Falls back to the default label ("Menu") when nothing matches.
const showCurrentPage = (toggle, list) => {
  const activeLink = list.querySelector("a.active")
  if (!activeLink) {
    return
  }

  // Expose the current page to assistive tech.
  activeLink.setAttribute("aria-current", "page")

  const label = toggle.querySelector(".sidebar-link-list-toggle-label") || toggle
  const currentText = activeLink.textContent.trim()
  if (currentText) {
    label.textContent = currentText
  }
}

export default () => {
  const toggles = Array.from(document.querySelectorAll(".sidebar-link-list-toggle"))

  if (!toggles.length) {
    return
  }

  // Guard against duplicate listeners if this initialiser runs more than once.
  if (sidebarLinkListController) {
    sidebarLinkListController.abort()
  }
  sidebarLinkListController = new AbortController()
  const { signal } = sidebarLinkListController

  toggles.forEach((toggle) => {
    const nav = toggle.closest(".sidebar-link-list")
    const listId = toggle.getAttribute("aria-controls")
    const list = listId ? document.getElementById(listId) : null

    if (!nav || !list) {
      return
    }

    showCurrentPage(toggle, list)

    toggle.addEventListener(
      "click",
      () => {
        const isOpen = toggle.getAttribute("aria-expanded") === "true"
        toggle.setAttribute("aria-expanded", String(!isOpen))

        // Safari (and Firefox) on macOS do not move focus to a <button> when it
        // is clicked. Without focus on the toggle, the nav-scoped keydown
        // listener never receives the arrow keys, so keyboard navigation
        // appears broken in Safari. Explicitly focusing the toggle keeps the
        // arrow/Home/End/Escape handling working after a mouse click too.
        toggle.focus()
      },
      { signal },
    )

    // Close on Escape and return focus to the toggle.
    nav.addEventListener(
      "keydown",
      (event) => {
        // Ignore keyboard handling on desktop, where the dropdown is inactive.
        if (!isDropdownActive(toggle)) {
          return
        }

        if (event.key === "Escape" && toggle.getAttribute("aria-expanded") === "true") {
          event.preventDefault()
          closeMenu(toggle)
          toggle.focus()
          return
        }

        focusLinkByArrow(event, toggle, list)
      },
      { signal },
    )

    // Close when clicking/tapping outside the navigation.
    document.addEventListener(
      "click",
      (event) => {
        if (toggle.getAttribute("aria-expanded") !== "true") {
          return
        }

        if (!nav.contains(event.target)) {
          closeMenu(toggle)
        }
      },
      { signal },
    )
  })
}
